/**
 * PlayerPreview — Admin view of the Android store player UI
 * Shows a phone-mockup styled now-playing view for any selected device,
 * with live broadcast queue (next up) and device telemetry.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Smartphone, SkipBack, SkipForward, Play, Pause, Volume2, VolumeX,
  Wifi, Battery, Signal, Lock, RefreshCw, ChevronDown, Music, Cpu,
  HardDrive, Clock, Activity, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { API_BASE } from '../services/apiBase';

const TENANT = '569593dd-38e2-4d69-bdca-5d07be8b46f1';
const API    = API_BASE;

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtAgo(iso) {
  if (!iso) return 'Never';
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fmtStorage(used, total) {
  if (!total) return null;
  const pct = Math.round((used / total) * 100);
  return { used: `${used} MB`, total: `${total} MB`, pct };
}

// ─── Animated waveform bars (idle/playing) ─────────────────────────────────
function Waveform({ playing, color = '#2f81f7' }) {
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center', height: 28 }}>
      {Array.from({ length: 12 }, (_, i) => (
        <div key={i} style={{
          width: 3, borderRadius: 2,
          background: playing ? color : color + '44',
          animationName: playing ? 'wave' : 'none',
          animationDuration: `${0.6 + (i % 3) * 0.15}s`,
          animationDelay: `${i * 0.05}s`,
          animationTimingFunction: 'ease-in-out',
          animationIterationCount: 'infinite',
          animationDirection: 'alternate',
          height: playing ? undefined : '30%',
        }} />
      ))}
    </div>
  );
}

// ─── Burn status dot ──────────────────────────────────────────────────────────
function BurnDot({ status }) {
  const c = status === 'clean' ? '#22c55e' : status === 'warning' ? '#f59e0b' : '#ef4444';
  return <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, display: 'inline-block', flexShrink: 0 }} />;
}

// ─── Phone frame mockup ───────────────────────────────────────────────────────
function PhoneMockup({ device, queue, volumeLocked, playing, onTogglePlay, onSkip }) {
  const [progress, setProgress] = useState(0);
  const [tick, setTick] = useState(0);
  const intervalRef = useRef(null);

  // Animate progress if playing
  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setProgress(p => {
          const next = p + (1 / 210); // ~3.5 min song
          return next >= 1 ? 0 : next;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing]);

  // Tick for "time ago" refresh
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const track = device?.extra?.current_track || {};
  const title  = track.title  || (device?.is_playing ? 'Now Playing' : 'Idle');
  const artist = track.artist || device?.name || 'InStore Radio';
  const duration = track.duration_seconds || 210;
  const elapsed  = Math.round(progress * duration);
  const elapsedStr  = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
  const durationStr = `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}`;

  // Status bar time
  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const isOnline = device?.status === 'online';

  return (
    <div style={{
      width: 320, flexShrink: 0,
      filter: 'drop-shadow(0 24px 48px rgba(0,0,0,0.6))',
    }}>
      {/* Phone outer shell */}
      <div style={{
        background: '#0f0f14',
        borderRadius: 40,
        padding: '12px 8px',
        border: '2px solid #2a2a3a',
        position: 'relative',
        boxShadow: 'inset 0 0 0 1px #3a3a4a, 0 0 0 1px #0a0a12',
      }}>
        {/* Side button */}
        <div style={{ position: 'absolute', right: -5, top: 100, width: 4, height: 60, background: '#1a1a2a', borderRadius: '0 2px 2px 0' }} />
        <div style={{ position: 'absolute', left: -5, top: 80,  width: 4, height: 40, background: '#1a1a2a', borderRadius: '2px 0 0 2px' }} />
        <div style={{ position: 'absolute', left: -5, top: 130, width: 4, height: 40, background: '#1a1a2a', borderRadius: '2px 0 0 2px' }} />

        {/* Screen */}
        <div style={{
          background: '#0d1117', borderRadius: 32, overflow: 'hidden',
          minHeight: 620, display: 'flex', flexDirection: 'column',
          position: 'relative',
        }}>
          {/* Status bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 20px 6px', fontSize: 12, fontWeight: 700, color: 'white',
          }}>
            <span>{timeStr}</span>
            <div style={{
              width: 80, height: 22, background: '#0d1117', borderRadius: 11,
              border: '2px solid #1a1a2a', position: 'absolute', left: '50%',
              transform: 'translateX(-50%)', top: 0,
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Signal size={12} color="white" />
              <Wifi size={12} color={isOnline ? 'white' : '#666'} />
              <Battery size={14} color="white" />
            </div>
          </div>

          {/* Offline banner */}
          {!isOnline && (
            <div style={{ background: '#ef444422', borderBottom: '1px solid #ef444444', padding: '6px 16px', fontSize: 11, color: '#ef4444', fontWeight: 700, textAlign: 'center', display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
              <AlertCircle size={11} /> Device offline · Last seen {fmtAgo(device?.last_heartbeat_at)}
            </div>
          )}

          {/* Gradient hero */}
          <div style={{
            flex: 1,
            background: 'linear-gradient(180deg, #1a1040 0%, #0d1117 60%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '16px 20px 0',
          }}>
            {/* Store label */}
            <div style={{ fontSize: 11, color: '#ffffff66', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>
              {device?.name || 'No Device Selected'}
            </div>

            {/* Album art */}
            <div style={{ position: 'relative', marginBottom: 24 }}>
              <div style={{
                width: 180, height: 180, borderRadius: 24,
                background: 'linear-gradient(135deg, #1a1040, #2f1060, #1a2060)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: playing
                  ? '0 20px 60px rgba(47, 129, 247, 0.3), 0 0 0 1px rgba(255,255,255,0.05)'
                  : '0 20px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
                transition: 'box-shadow 0.5s',
              }}>
                <Music size={64} color="rgba(255,255,255,0.15)" strokeWidth={1} />
                {/* Vinyl ring */}
                <div style={{
                  position: 'absolute', width: 40, height: 40, borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.1)', top: '50%', left: '50%',
                  transform: 'translate(-50%,-50%)',
                }} />
              </div>
              {/* Playing pulse ring */}
              {playing && (
                <div style={{
                  position: 'absolute', inset: -8, borderRadius: 32,
                  border: '2px solid #2f81f744',
                  animation: 'pulse-ring 2s ease-out infinite',
                }} />
              )}
            </div>

            {/* Track info */}
            <div style={{ textAlign: 'center', marginBottom: 8, width: '100%', padding: '0 8px' }}>
              <div style={{
                fontSize: 18, fontWeight: 800, color: 'white', lineHeight: 1.2, marginBottom: 4,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{title}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>{artist}</div>
            </div>

            {/* Waveform */}
            <div style={{ marginBottom: 16, opacity: playing ? 1 : 0.3, transition: 'opacity 0.3s' }}>
              <Waveform playing={playing} color="#2f81f7" />
            </div>

            {/* Progress bar */}
            <div style={{ width: '100%', marginBottom: 8 }}>
              <div style={{
                height: 3, background: 'rgba(255,255,255,0.15)', borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
              }}>
                <div style={{
                  height: '100%', width: `${progress * 100}%`,
                  background: 'linear-gradient(90deg, #2f81f7, #8b5cf6)',
                  borderRadius: 2, transition: 'width 0.5s linear',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                <span>{elapsedStr}</span>
                <span>{durationStr}</span>
              </div>
            </div>

            {/* Transport controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 20, marginTop: 4 }}>
              <button onClick={() => onSkip(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', padding: 6 }}>
                <SkipBack size={22} />
              </button>
              <button onClick={onTogglePlay} style={{
                width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #2f81f7, #8b5cf6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(47,129,247,0.4)',
                transition: 'transform 0.1s',
              }}>
                {playing ? <Pause size={22} color="white" /> : <Play size={22} color="white" fill="white" />}
              </button>
              <button onClick={() => onSkip(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', padding: 6 }}>
                <SkipForward size={22} />
              </button>
            </div>

            {/* Volume lock */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', borderRadius: 999,
              background: volumeLocked ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)',
              marginBottom: 16,
            }}>
              {volumeLocked
                ? <><Lock size={12} color="#ef4444" /><span style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}>Volume Locked</span><VolumeX size={12} color="#ef4444" /></>
                : <><Volume2 size={12} color="rgba(255,255,255,0.4)" /><span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Volume Control Active</span></>
              }
            </div>

            {/* Next up */}
            {queue.length > 0 && (
              <div style={{ width: '100%', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8, paddingLeft: 2 }}>Next Up</div>
                {queue.slice(0, 3).map((item, i) => (
                  <div key={item.id || i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '7px 2px', borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  }}>
                    <BurnDot status={item.burn_status} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title || item.track?.title || 'Unknown Track'}
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.artist || item.track?.artist || '—'}
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}>
                      {item.duration_seconds ? `${Math.floor(item.duration_seconds / 60)}:${String(item.duration_seconds % 60).padStart(2,'0')}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Home bar */}
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 10, paddingTop: 8, background: '#0d1117' }}>
            <div style={{ width: 100, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Device stat row ──────────────────────────────────────────────────────────
function StatRow({ icon: Icon, label, value, accent, bar }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <Icon size={13} color={accent || 'var(--text-muted)'} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>{label}</span>
      <div style={{ textAlign: 'right' }}>
        {bar ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 60, height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${bar}%`, background: bar > 90 ? '#ef4444' : bar > 70 ? '#f59e0b' : accent || '#2f81f7', borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</span>
          </div>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 600, color: accent || 'var(--text-primary)' }}>{value}</span>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function PlayerPreview() {
  const [stores,  setStores]  = useState([]);
  const [devices, setDevices] = useState([]);
  const [queue,   setQueue]   = useState([]);
  const [storeId,  setStoreId]  = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [device,   setDevice]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [playing,  setPlaying]  = useState(false);
  const [volumeLocked, setVolumeLocked] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const autoRefreshRef = useRef(null);

  // Load stores
  useEffect(() => {
    axios.get(`${API}/tenants/${TENANT}/stores`, { headers: authHeaders() })
      .then(r => {
        const list = r.data || [];
        setStores(list);
        if (list.length) setStoreId(list[0].id);
      })
      .catch(() => toast.error('Failed to load stores'));
  }, []);

  // Load devices when store changes
  useEffect(() => {
    if (!storeId) return;
    setDevices([]); setDeviceId(''); setDevice(null);
    axios.get(`${API}/devices/store/${storeId}`, { headers: authHeaders() })
      .then(r => {
        const list = r.data || [];
        setDevices(list);
        if (list.length) setDeviceId(list[0].id);
      })
      .catch(() => {});
  }, [storeId]);

  const loadDevice = useCallback(async (did, sid) => {
    if (!did) return;
    setLoading(true);
    try {
      const [dRes, qRes] = await Promise.allSettled([
        axios.get(`${API}/devices/${did}`, { headers: authHeaders() }),
        axios.get(`${API}/broadcast-queue`, { headers: authHeaders(), params: { tenant_id: TENANT, store_id: sid, size: 10 } }),
      ]);
      if (dRes.status === 'fulfilled') {
        const d = dRes.value.data;
        setDevice(d);
        setPlaying(d.is_playing || false);
      }
      if (qRes.status === 'fulfilled') setQueue(qRes.value.data || []);
      setLastRefresh(new Date());
    } catch { toast.error('Failed to load device'); }
    finally { setLoading(false); }
  }, []);

  // Load device when deviceId changes
  useEffect(() => {
    if (deviceId) loadDevice(deviceId, storeId);
  }, [deviceId, storeId, loadDevice]);

  // Auto-refresh every 30s
  useEffect(() => {
    clearInterval(autoRefreshRef.current);
    if (deviceId) {
      autoRefreshRef.current = setInterval(() => loadDevice(deviceId, storeId), 30000);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [deviceId, storeId, loadDevice]);

  const storage = device ? fmtStorage(device.storage_used_mb, device.storage_total_mb) : null;
  const isOnline = device?.status === 'online';

  return (
    <div>
      <style>{`
        @keyframes wave {
          from { height: 20%; }
          to   { height: 80%; }
        }
        @keyframes pulse-ring {
          0%   { transform: scale(1);   opacity: 0.6; }
          100% { transform: scale(1.08); opacity: 0; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Smartphone size={20} color="var(--accent)" /> Player Preview
          </h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>Live view of what's playing on a store device</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastRefresh && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Refreshed {fmtAgo(lastRefresh)}</span>}
          <button className="btn btn-ghost" onClick={() => loadDevice(deviceId, storeId)} disabled={!deviceId || loading}>
            <RefreshCw size={13} style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {/* Selectors */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 32, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Store</label>
          <div style={{ position: 'relative' }}>
            <select className="form-select" style={{ width: 200, paddingRight: 32 }}
              value={storeId} onChange={e => setStoreId(e.target.value)}>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Device</label>
          <div style={{ position: 'relative' }}>
            <select className="form-select" style={{ width: 220, paddingRight: 32 }}
              value={deviceId} onChange={e => setDeviceId(e.target.value)}
              disabled={!devices.length}>
              {devices.length === 0
                ? <option>No devices</option>
                : devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)
              }
            </select>
            <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
          </div>
        </div>
        {/* Status indicator */}
        {device && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, background: isOnline ? '#22c55e18' : '#6b728018', marginLeft: 4 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: isOnline ? '#22c55e' : '#6b7280', boxShadow: isOnline ? '0 0 8px #22c55e' : 'none' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: isOnline ? '#22c55e' : '#6b7280' }}>{isOnline ? 'Online' : 'Offline'}</span>
          </div>
        )}
      </div>

      {/* Main layout */}
      <div style={{ display: 'flex', gap: 40, alignItems: 'flex-start' }}>

        {/* Phone mockup */}
        {device ? (
          <PhoneMockup
            device={device}
            queue={queue}
            volumeLocked={volumeLocked}
            playing={playing}
            onTogglePlay={() => setPlaying(p => !p)}
            onSkip={() => {}}
          />
        ) : (
          <div style={{
            width: 320, height: 600, background: 'var(--bg-surface)', borderRadius: 40,
            border: '2px solid var(--border)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', gap: 12,
          }}>
            <Smartphone size={48} style={{ opacity: 0.2 }} />
            <div style={{ fontSize: 13 }}>{loading ? 'Loading…' : 'Select a device above'}</div>
          </div>
        )}

        {/* Right panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>

          {/* Device telemetry */}
          {device && (
            <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Cpu size={14} color="var(--accent)" />
                <span style={{ fontSize: 13, fontWeight: 700 }}>Device Telemetry</span>
              </div>
              <div style={{ padding: '4px 18px 12px' }}>
                <StatRow icon={Activity}   label="Status"        value={device.status}          accent={isOnline ? '#22c55e' : '#6b7280'} />
                <StatRow icon={Clock}      label="Last Heartbeat" value={fmtAgo(device.last_heartbeat_at)} />
                <StatRow icon={Smartphone} label="App Version"   value={device.app_version || '—'} />
                <StatRow icon={Cpu}        label="OS Version"    value={device.os_version || '—'} />
                {storage && <StatRow icon={HardDrive} label="Storage" value={`${storage.used} / ${storage.total}`} bar={storage.pct} accent="#2f81f7" />}
                <StatRow icon={Wifi}       label="IP Address"    value={device.ip_address || '—'} />
              </div>
            </div>
          )}

          {/* Volume lock toggle */}
          <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Lock size={14} color={volumeLocked ? '#ef4444' : 'var(--text-muted)'} />
                  <span style={{ fontSize: 14, fontWeight: 700 }}>Volume Lock</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 260 }}>
                  When enabled, store staff cannot adjust volume on the player device.
                </div>
              </div>
              {/* Toggle */}
              <div
                onClick={() => setVolumeLocked(v => !v)}
                style={{
                  width: 48, height: 26, borderRadius: 13, cursor: 'pointer', flexShrink: 0,
                  background: volumeLocked ? '#ef4444' : 'var(--bg-elevated)',
                  border: `1px solid ${volumeLocked ? '#ef4444' : 'var(--border)'}`,
                  position: 'relative', transition: 'all 0.2s',
                }}>
                <div style={{
                  position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%',
                  background: 'white', transition: 'left 0.2s',
                  left: volumeLocked ? 26 : 4,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                }} />
              </div>
            </div>
          </div>

          {/* Full queue list */}
          {queue.length > 0 && (
            <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Music size={14} color="var(--accent)" />
                <span style={{ fontSize: 13, fontWeight: 700 }}>Broadcast Queue</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{queue.length} tracks</span>
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {queue.map((item, i) => (
                  <div key={item.id || i} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '9px 18px', borderBottom: '1px solid var(--border)',
                    background: i === 0 ? 'var(--bg-elevated)' : 'transparent',
                  }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 18, textAlign: 'right', fontWeight: 600 }}>
                      {i === 0 ? '▶' : i + 1}
                    </span>
                    <BurnDot status={item.burn_status} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title || item.track?.title || 'Unknown'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.artist || item.track?.artist || '—'}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                      {item.estimated_play_time || ''}
                    </div>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 700,
                      background: item.burn_status === 'clean' ? '#22c55e18' : item.burn_status === 'warning' ? '#f59e0b18' : '#ef444418',
                      color:      item.burn_status === 'clean' ? '#22c55e'   : item.burn_status === 'warning' ? '#f59e0b'   : '#ef4444',
                    }}>{item.burn_status || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
